import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * Hand-curated API changelog. Every entry MUST be additive — we never
 * remove fields or change endpoint shapes. If you're tempted to break
 * a contract, version a new endpoint instead and leave the old one.
 *
 * Newest first. Bump CURRENT_VERSION when you ship the next entry.
 */
const CURRENT_VERSION = "v2026.08";

const CHANGELOG = [
  {
    version: "v2026.08",
    date: "2026-08-19",
    headline: "Click + conversion APIs match the publisher dashboard",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/reports/clicks-log",
        change:
          "Same builder as Reports → Click Log. Commission is USD and is populated when the network sends a payout with no cart/order amount (sale_amount=0). Page 1 includes `totals` matching the dashboard tiles.",
      },
      {
        endpoint: "GET /v1/reports/conversions",
        change:
          "Same earnings engine as Commission Level / Payouts. Sale amount may be $0; `commission` is still the payout-based USD earning. Page 1 includes dashboard-matching `totals`. Pagination is uniquely sorted by event_date + action_id so concatenated pages match the dashboard commission CSV (no skipped or duplicated conversions). Optional `cursor` / `next_cursor`. `status` uses the dashboard CSV labels (REJECTED, not REVERSED); `status=REJECTED` and `status=REVERSED` both filter the same rows.",
      },
    ],
  },
  {
    version: "v2026.07",
    date: "2026-07-24",
    headline: "Date + brand filters on brand click rollup",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/reports/clicks",
        change:
          "Optional `start_date` / `end_date` (YYYY-MM-DD in account timezone) and `brand` filter — same shape as `/v1/reports/conversions`. Response includes top-level `timezone`.",
      },
    ],
  },
  {
    version: "v2026.05",
    date: "2026-05-06",
    headline: "Publisher commission visible on every conversion",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/reports/clicks",
        change:
          "New `earnings` field on every brand row — your computed payout cut, summed across non-reversed conversions for that brand. Old fields untouched.",
      },
      {
        endpoint: "GET /v1/reports/commissions",
        change:
          "Now returns PENDING + APPROVED conversions (was APPROVED-only). New `cleared_for_payout` boolean per row — `true` when admin has explicitly cleared the action for payout, `false` for accruing rows. Sum APPROVED + cleared rows for billing-ready totals.",
      },
    ],
  },
  {
    version: "v2026.04",
    date: "2026-04-22",
    headline: "Pagination on click-log + admin actions",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/reports/clicks-log",
        change:
          "Optional `page` and `page_size` query params (default 100, max 1000). When `page` is set, response wraps `data[]` with `page`, `page_size`, `total`, `total_pages`. Legacy callers (no `page`) keep the original unwrapped shape.",
      },
    ],
  },
  {
    version: "v2026.03",
    date: "2026-04-09",
    headline: "Tracker slots — multiple sub_ids per publisher",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/me",
        change:
          "New endpoint. Returns `{sub_id, slot_sub_ids[]}` so multi-channel publishers can see every active sub_id their account owns.",
      },
      {
        endpoint: "GET /v1/brands",
        change:
          "Now returns one row per (brand × slot) instead of one row per brand. Each row's `tracking_link` is pre-stamped with the matching slot sub_id.",
      },
      {
        endpoint: "All row-level reports",
        change:
          "Added `sub_id1` to every row so you can attribute traffic back to the slot that produced it.",
      },
    ],
  },
  {
    version: "v2026.02",
    date: "2026-03-18",
    headline: "Conversions endpoint for click→sale reconciliation",
    type: "additive",
    items: [
      {
        endpoint: "GET /v1/reports/conversions",
        change:
          "New endpoint. One row per conversion with both click side (`rid`, `click_id`, `click_date`) and commission side (`action_id`, `sale_amount`, `commission`, `status`) joined — match your stored click_id against the sale in a single call.",
      },
    ],
  },
];

export function ApiChangelogBanner() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50/60 mb-4"
        data-testid="api-changelog-banner"
      >
        <div className="flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-emerald-600 shrink-0" />
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
            API
          </span>
          <span className="font-mono text-[11px] font-bold bg-white text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded">
            {CURRENT_VERSION}
          </span>
          <span className="text-xs text-slate-600">— additive changes only · zero breaking releases</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs font-semibold text-[#002FA7] hover:underline"
          data-testid="api-changelog-open"
        >
          What's new →
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="api-changelog-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-emerald-600" />
              API Changelog
            </DialogTitle>
            <DialogDescription>
              Every entry is additive. We never remove a field or change an existing
              response shape — new behavior is opt-in via new params or new fields.
              You can ship integrations once and forget.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-2">
            {CHANGELOG.map((release, idx) => (
              <div
                key={release.version}
                className="border-l-2 border-[#002FA7]/30 pl-4 relative"
                data-testid={`changelog-release-${release.version}`}
              >
                <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#002FA7]" />
                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                  <span className="font-mono text-[11px] font-bold text-[#002FA7] bg-[#002FA7]/5 border border-[#002FA7]/20 px-1.5 py-0.5 rounded">
                    {release.version}
                  </span>
                  <span className="text-[11px] text-slate-500 font-mono">{release.date}</span>
                  {idx === 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle size={8} weight="fill" /> Current
                    </span>
                  )}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                    {release.type}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">{release.headline}</h3>
                <ul className="space-y-2">
                  {release.items.map((it, i) => (
                    <li key={i} className="text-xs text-slate-600 leading-relaxed">
                      <code className="font-mono bg-slate-100 text-slate-900 px-1.5 py-0.5 rounded text-[11px]">
                        {it.endpoint}
                      </code>
                      <span className="ml-2">{it.change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200 text-[11px] text-slate-500">
            Spotted a regression? Email{" "}
            <a href="mailto:support@theredclub.com" className="text-[#002FA7] hover:underline">
              support@theredclub.com
            </a>{" "}
            with your API key prefix and the request URL — we'll respond within one business day.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
